import { App, Plugin, Ref, ref } from "vue";
import {
  Configuration as BackendConfiguration,
  ConfigurationParameters,
  ReportingApi,
  ReportingApiInterface,
  BoostrackApi,
  BoostrackApiInterface,
} from "@apis/backend";
import { AccessToken, isValidAccessToken } from "./apiHelpers";
import { ApiMiddleware } from "./ApiMiddleware";
import {
  BaseApis,
  setupCallWhenTokenInitialized,
} from "@/plugins/baseApiPlugin";

export interface BackendApis extends BaseApis {
  reporting: ReportingApiInterface;
  boostrack: BoostrackApiInterface;
  callWhenTokenInitialized: (toCall: () => void | (() => void)[]) => void;
}

export const apiPlugin: Plugin = {
  install: (app: App) => {
    app.config.globalProperties.$backendApiAccessToken = ref<AccessToken>();
    app.config.globalProperties.$backendApiAccessTokenLock = ref(false);

    const accessTokenToHeader: (at?: AccessToken) => string = (at) => {
      if (isValidAccessToken(at)) {
        return "Bearer " + at!.accessToken;
      } else {
        throw new Error("BackendApi: Missing access token");
      }
    };

    const configBase: ConfigurationParameters = {
      middleware: [new ApiMiddleware(app.config.globalProperties.$language)],
    };
    const backendConfig = new BackendConfiguration({
      ...configBase,
      basePath: "/api/backend",
      accessToken: () =>
        accessTokenToHeader(
          app.config.globalProperties.$backendApiAccessToken.value,
        ),
    });

    const isTokenInitialized = ref(false);

    app.config.globalProperties.$backendApis = {
      reporting: new ReportingApi(backendConfig),
      boostrack: new BoostrackApi(backendConfig),

      isTokenInitialized: isTokenInitialized,
      isTokenCurrentlyValid: ref(false),
      callWhenTokenInitialized:
        setupCallWhenTokenInitialized(isTokenInitialized),
    };
  },
};

declare module "vue" {
  interface ComponentCustomProperties {
    $backendApiAccessToken: Ref<AccessToken | undefined>;
    $backendApis: BackendApis;
    $backendApiAccessTokenLock: Ref<boolean>;
  }
}
